import React, { useState, VoidFunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { StringUtils } from 'record-it-utils/utils/StringUtils/StringUtils';
import {
  ButtonStandard,
  ButtonStandardType
} from '../../button/ButtonStandard/ButtonStandard';
import { useEventCallback } from '../../hooks/useEventCallback';
import { useModalLifecycleEffect } from '../../hooks/useModalLifecycleEffect/useModalLifecycleEffect';
import {
  ConfirmModal,
  ConfirmModalProps,
  ConfirmModalWidthType
} from '../../modal/ConfirmModal/ConfirmModal';
import { ConfirmModalBody } from '../../modal/ConfirmModalBody/ConfirmModalBody';
import {
  ConfirmModalButtonFooter,
  ConfirmModalButtonFooterButtonAlignment
} from '../../modal/ConfirmModalButtonFooter/ConfirmModalButtonFooter';
import { ConfirmModalHeader } from '../../modal/ConfirmModalHeader/ConfirmModalHeader';
import { prepareProps } from '../../utils/propUtils';
import { PropsWithoutChildren } from '../../utils/utilityTypes';
import { ClockPicker, ClockPickerProps } from '../ClockPicker/ClockPicker';
import styles from './TimeClockPickerModal.module.css';

export type TimeClockPickerModalProps = Omit<
  PropsWithoutChildren<ConfirmModalProps>,
  'widthType'
> & {
  hours: number | null;
  minutes: number | null;
  onClose: () => void;
  onValueChanged: (hours: number, minutes: number) => void;
};

export const TimeClockPickerModal: VoidFunctionComponent<TimeClockPickerModalProps> =
  React.memo((props) => {
    const {
      hours,
      minutes,
      opened,
      onClose,
      onValueChanged,
      ...remainingProps
    } = prepareProps({ props, ignoreChildren: true });

    const [internalHours, setInternalHours] = useState(hours);
    const [internalMinutes, setInternalMinutes] = useState(minutes);
    const [mode, setMode] = useState<'hours' | 'minutes'>('hours');
    const { t } = useTranslation('record-it-components');

    const callOnValueChanged = useEventCallback(
      (newHours: number | null, newMinutes: number | null) => {
        const newHoursWithFallback = newHours ?? 0;
        const newMinutesWithFallback = newMinutes ?? 0;
        if (
          newHoursWithFallback !== hours ||
          newMinutesWithFallback !== minutes
        ) {
          onValueChanged(newHoursWithFallback, newMinutesWithFallback);
        }
      }
    );

    const reset = useEventCallback(() => {
      setMode('hours');
      setInternalHours(hours);
      setInternalMinutes(minutes);
    });

    useModalLifecycleEffect({
      opened,
      afterOpened: reset
    });

    const handleHoursClockPickerSelected: ClockPickerProps['onValueChanged'] =
      useEventCallback((newHours) => {
        setInternalHours(newHours);
        setMode('minutes');
      });

    const handleMinutesClockPickerSelected: ClockPickerProps['onValueChanged'] =
      useEventCallback((newMinutes) => {
        setInternalMinutes(newMinutes);
        callOnValueChanged(internalHours, newMinutes);
        onClose();
      });

    const handleCancelClick = useEventCallback(() => {
      reset();
      onClose();
    });

    const handleNextClick = useEventCallback(() => {
      setMode('minutes');
    });

    const handlePreviousClick = useEventCallback(() => {
      setMode('hours');
    });

    const handleFinishClick = useEventCallback(() => {
      callOnValueChanged(internalHours, internalMinutes);
      onClose();
    });

    return (
      <ConfirmModal
        {...remainingProps}
        widthType={ConfirmModalWidthType.Auto}
        opened={opened}
      >
        <ConfirmModalHeader>
          <span
            className={styles.timePreview}
            data-testid="TimeClockPickerModal.TimePreview"
          >
            <span
              className={styles.timePreviewPart}
              data-active={mode === 'hours' ? 'true' : 'false'}
              data-testid="TimeClockPickerModal.TimePreviewHours"
            >
              {StringUtils.padLeft(internalHours?.toString() ?? '--', 2)}
            </span>
            :
            <span
              className={styles.timePreviewPart}
              data-active={mode === 'minutes' ? 'true' : 'false'}
              data-testid="TimeClockPickerModal.TimePreviewMinutes"
            >
              {StringUtils.padLeft(internalMinutes?.toString() ?? '--', 2)}
            </span>
          </span>
        </ConfirmModalHeader>
        <ConfirmModalBody>
          {mode === 'hours' ? (
            <ClockPicker
              key="hoursClockPicker"
              value={internalHours ?? 0}
              tickConfiguration={hoursTickConfiguration}
              onValueSelected={handleHoursClockPickerSelected}
              className={styles.clockPicker}
              data-testid="TimeClockPickerModal.HoursClockPicker"
            ></ClockPicker>
          ) : null}
          {mode === 'minutes' ? (
            <ClockPicker
              key="minutesClockPicker"
              value={internalMinutes ?? 0}
              tickConfiguration={minutesTickConfiguration}
              onValueSelected={handleMinutesClockPickerSelected}
              className={styles.clockPicker}
              data-testid="TimeClockPickerModal.MinutesClockPicker"
            ></ClockPicker>
          ) : null}
        </ConfirmModalBody>
        <ConfirmModalButtonFooter
          buttonAlignment={ConfirmModalButtonFooterButtonAlignment.SpaceBetween}
        >
          {mode === 'hours' ? (
            <>
              <ButtonStandard
                onClick={handleCancelClick}
                data-testid="TimeClockPickerModal.CancelButton"
              >
                {t('dateTime.TimeClockPickerModal.cancel')}
              </ButtonStandard>
              <ButtonStandard
                type={ButtonStandardType.Primary}
                onClick={handleNextClick}
                data-testid="TimeClockPickerModal.NextButton"
              >
                {t('dateTime.TimeClockPickerModal.next')}
              </ButtonStandard>
            </>
          ) : null}
          {mode === 'minutes' ? (
            <>
              <ButtonStandard
                onClick={handlePreviousClick}
                data-testid="TimeClockPickerModal.PreviousButton"
              >
                {t('dateTime.TimeClockPickerModal.previous')}
              </ButtonStandard>
              <ButtonStandard
                type={ButtonStandardType.Primary}
                onClick={handleFinishClick}
                data-testid="TimeClockPickerModal.FinishButton"
              >
                {t('dateTime.TimeClockPickerModal.finish')}
              </ButtonStandard>
            </>
          ) : null}
        </ConfirmModalButtonFooter>
      </ConfirmModal>
    );
  });

const hoursTickConfiguration: ClockPickerProps['tickConfiguration'] = {
  outerTicks: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11],
  innerTicks: [12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23]
};

const minutesTickConfiguration: ClockPickerProps['tickConfiguration'] = {
  outerTicks: [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55],
  innerTicks: []
};
